// endpointConfigApi.js — the GetEndpointConfigState client (gRPC + JSON gateway,
// gated by tenant.endpoint_metadata.read). Reads the AI coding-agent config in
// effect on one endpoint plus the observation history behind it, backing the
// endpoint-subject signal detail pane.
//
// REST rather than the analytics graph, matching how every other per-entity
// state/history panel in this dashboard reads (endpoint metadata, directory
// sync-history, signal versions). Two reasons beyond conformance: the analytics
// planner rejects absolute ranges wider than 90 days, and the current-state
// projection has NO TTL — a stable endpoint's last config observation can be
// arbitrarily old, so a windowed read would silently drop targets that simply
// haven't changed. The graph would also expose these tables to every MCP
// customer, which is a product decision this pane doesn't need to make.
//
// The endpoint id crosses an id-form boundary here: callers start from an
// analytics-sourced hyphenated UUID, and the proto `bytes` field wants base64 —
// hence `uuidToEndpointId` at the call site.
import { authedFetch } from "./restClient.js";

// grpc-gateway's default JSON encoder emits camelCase; accept snake_case too, the
// same dual-shape guard signalsApi / userInfo / permissions carry.
function pick(obj, snake, camel) {
    if (!obj) return undefined;
    return obj[camel] ?? obj[snake];
}

function strArray(v) {
    return Array.isArray(v) ? v.filter((s) => typeof s === "string") : [];
}

function adaptTargetState(t) {
    return {
        configTargetId: String(pick(t, "config_target_id", "configTargetId") ?? ""),
        agentProvider: String(pick(t, "agent_provider", "agentProvider") ?? ""),
        layer: String(t?.layer ?? ""),
        sourcePath: String(pick(t, "source_path", "sourcePath") ?? ""),
        contentHash: String(pick(t, "content_hash", "contentHash") ?? ""),
        exists: Boolean(t?.exists ?? false),
        observedAt: String(pick(t, "observed_at", "observedAt") ?? ""),
        // The config body, bounded agent-side. Only ever present on current
        // state — history rows omit it, because at up to 512 KiB apiece a full
        // window would be a ~100 MB response.
        configContentJson: String(pick(t, "config_content_json", "configContentJson") ?? ""),
    };
}

function adaptChange(c) {
    return {
        eventId: String(pick(c, "event_id", "eventId") ?? ""),
        configTargetId: String(pick(c, "config_target_id", "configTargetId") ?? ""),
        agentProvider: String(pick(c, "agent_provider", "agentProvider") ?? ""),
        layer: String(c?.layer ?? ""),
        sourcePath: String(pick(c, "source_path", "sourcePath") ?? ""),
        contentHash: String(pick(c, "content_hash", "contentHash") ?? ""),
        exists: Boolean(c?.exists ?? false),
        observationSource: String(pick(c, "observation_source", "observationSource") ?? ""),
        writerOperation: String(pick(c, "writer_operation", "writerOperation") ?? ""),
        writerImagePath: String(pick(c, "writer_image_path", "writerImagePath") ?? ""),
        changePreviousExists: Boolean(pick(c, "change_previous_exists", "changePreviousExists") ?? false),
        changePreviousContentHash: String(pick(c, "change_previous_content_hash", "changePreviousContentHash") ?? ""),
        addedKeys: strArray(pick(c, "change_added_keys", "changeAddedKeys")),
        updatedKeys: strArray(pick(c, "change_updated_keys", "changeUpdatedKeys")),
        removedKeys: strArray(pick(c, "change_removed_keys", "changeRemovedKeys")),
        observedAt: String(pick(c, "observed_at", "observedAt") ?? ""),
    };
}

// Writer attribution requires both a file-event source and the writer payload's
// operation. Read-triggered self-healing observations are FILE_EVENT rows too,
// but deliberately have no writer payload; startup scans and MDM polls are also
// writer-less.
const FILE_EVENT_SOURCE = "CONFIG_OBSERVATION_SOURCE_FILE_EVENT";
const UNSPECIFIED_WRITER_OPERATION = "CONFIG_WRITER_OPERATION_UNSPECIFIED";

export function isWriterAttributed(change) {
    return (
        change?.observationSource === FILE_EVENT_SOURCE &&
        Boolean(change.writerOperation) &&
        change.writerOperation !== UNSPECIFIED_WRITER_OPERATION
    );
}

// The most recent attributed write among `changes` (assumed newest-first), or
// null when every observation in the window is writer-less. Use this for "who
// last changed this" rather than `changes[0]`, which may be a re-send or read.
export function lastAttributedWrite(changes = []) {
    return changes.find(isWriterAttributed) ?? null;
}

// POST /v1/endpoints/config-state:get → { current, history } for one endpoint.
// `endpointId` must be the base64 (proto bytes) form. `since` / `until` are ISO
// strings bounding the history window only — current state is always unwindowed
// server-side. Throws with `.status` set so a 403 (missing
// tenant.endpoint_metadata.read) is distinguishable from a transport error.
export async function getEndpointConfigState(endpointId, { since, until, historyLimit = 200, configTargetId } = {}) {
    const body = { endpointId, historyLimit };
    if (since) body.since = since;
    if (until) body.until = until;
    // Narrows `history` to one config target. (tenant, endpoint, target) is an
    // exact primary-key prefix, so this is an index range scan — worth using when
    // one target's deep history matters more than a shallow view of them all.
    if (configTargetId) body.configTargetId = configTargetId;
    const res = await authedFetch("/v1/endpoints/config-state:get", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = new Error(`GetEndpointConfigState ${res.status}: ${res.statusText}`);
        err.status = res.status;
        throw err;
    }
    const json = await res.json();
    return {
        current: (Array.isArray(json.current) ? json.current : []).map(adaptTargetState),
        history: (Array.isArray(json.history) ? json.history : []).map(adaptChange),
    };
}
